import React, { useCallback } from 'react';
import { useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Tooltip } from '@mui/material';
import { useAppSelector } from '../../redux/hooks';
import { userDataSelectors } from '../../redux/userData';

interface Props {
  to: string;
  icon: JSX.Element;
  title: string;
  open: boolean;
  isExternal?: boolean;
}

const NavigationLink: React.FC<Props> = ({
  to,
  icon,
  isExternal,
  title,
  open,
}) => {
  const navigate = useNavigate();
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: true });
  const user = useAppSelector(userDataSelectors.getUserData);

  const handleOnClick = useCallback(() => {
    if (isExternal) {
      window.open(to, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(to);
  }, [navigate, isExternal, to]);

  if (!user) {
    return null;
  }
  const {
    canManageKpiDefinitions,
    isFinance,
    hasAccounts,
    isBillRateManager,
    isCompensationManager,
  } = user.permissions;

  const hasFullFeatureAccess =
    hasAccounts &&
    (isBillRateManager || isCompensationManager) &&
    canManageKpiDefinitions &&
    isFinance;

  return (
    <ListItemButton
      selected={Boolean(match)}
      onClick={handleOnClick}
      sx={{
        pl: 0,
        py: hasFullFeatureAccess ? 0 : 1,
        borderRadius: 1,
        pr: open ? 2 : 0,
        justifyContent: open ? 'flex-start' : 'center',
      }}
    >
      <Tooltip title={title} placement="right" disableHoverListener={open}>
        <ListItemIcon sx={{ justifyContent: 'center' }}>{icon}</ListItemIcon>
      </Tooltip>

      <ListItemText
        primary={title}
        sx={{
          whiteSpace: 'nowrap',
          opacity: open ? 1 : 0,
          width: open ? 'auto' : 0,
          transition: open
            ? 'opacity 175ms ease-out 200ms, width 150ms ease-out'
            : 'opacity 100ms ease-in, width 100ms ease-in',
        }}
      />
    </ListItemButton>
  );
};

export default NavigationLink;
